import React, { useEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import DateTimePicker from "react-datetime-picker";
import "react-datetime-picker/dist/DateTimePicker.css";
import { useNavigate } from "react-router-dom";

import Breadcrumb from "../../common/components/Breadcrumb";
import CenteredLoader from "../../common/components/CenteredLoader";
import { createAbortController, mergeErrors } from "../../common/utils";
import "./styles/main.css";
import { FormErrorsSchema } from "../../common/types";
import { Input } from "../../common/components/Form/Input";
import { Button } from "../../common/components/Form/Button";
import { Checkbox } from "../../common/components/Form/Checkbox";
import ImprovedDatePicker from "../../common/components/Form/ImprovedDatePicker";
import notifications from "../../common/services/notifications";
import AuthProvider from "../../common/services/auth/AuthProvider/Index";

// Shared utilities and components
import {
  changeStatus,
  fetchInProgressData,
  fetchResults,
  generateUpsReturnLabel,
  updateTrackingNumber,
  AnalysisKitDataSchema,
} from "./utils/api";
// import { useShippingAddress } from "./hooks/useShippingAddress";
// import { LabelAddressForm } from "./components/LabelAddressForm";

interface ProgressBarProps {
  step: number;
}

interface InitialStepProps {
  fetchData: Function;
  step: number;
}

interface StepProps {
  fetchData: Function;
  step: number;
  data: AnalysisKitDataSchema;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ step }) => {
  const steps = [
    {
      number: 1,
      title: "Claim Your Kit",
      description: "Enter your kit details",
    },
    {
      number: 2,
      title: "Confirm Shipping",
      description: "Verify tracking number",
    },
    { number: 3, title: "Collect Sample", description: "Follow instructions" },
    { number: 4, title: "Mail Sample", description: "Send to laboratory" },
  ];

  return (
    <div className="mb-8">
      {/* Step Title */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-4 mb-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            {step === 1 && "Step 1 of 4: Claim Your Kit"}
            {step === 2 && "Step 2 of 4: Confirm Your Shipping Label"}
            {step === 3 && "Step 3 of 4: Collect Your Sample"}
            {step === 4 && "Step 4 of 4: Mail Your Sample"}
          </h1>
        </div>
        <p className="text-gray-600 text-sm md:text-base">
          {step === 1 &&
            "Follow the steps below to ensure your kit is properly registered and your results are delivered"}
          {step === 2 && "Please verify your shipping information is correct"}
          {step === 3 &&
            "Follow the detailed instructions to collect your sample"}
          {step === 4 && "Package and send your sample to our laboratory"}
        </p>
      </div>

      {/* Enhanced Progress Bar */}
      <div className="relative">
        {/* Desktop Progress Bar */}
        <div className="hidden md:flex items-center justify-between w-full max-w-4xl mx-auto relative">
          {/* Individual line segments between steps */}
          {steps.map((stepItem, index) => {
            if (index === steps.length - 1) return null; // No line after last step

            const isCompleted = step > stepItem.number;

            return (
              <div
                key={`line-${stepItem.number}`}
                className="absolute top-6 h-1 z-0 transition-all duration-1000 ease-out"
                style={{
                  left: `${12.5 + index * 25}%`,
                  width: "25%",
                  backgroundColor: isCompleted ? "#2c91cb" : "#e5e7eb",
                }}
              />
            );
          })}

          {steps.map((stepItem, index) => (
            <div
              key={stepItem.number}
              className="flex flex-col items-center flex-1 relative"
            >
              {/* Step Circle */}
              <div className="relative z-20">
                <div
                  className={`
                  w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold
                  transition-all duration-300 ease-in-out transform
                  ${
                    step >= stepItem.number
                      ? "bg-gradient-to-r from-brand-blue-500 to-brand-blue-600 text-white shadow-lg scale-110"
                      : "bg-gray-200 text-gray-500 hover:bg-gray-300"
                  }
                `}
                >
                  {step > stepItem.number ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    stepItem.number
                  )}
                </div>
              </div>

              {/* Step Info */}
              <div className="mt-3 text-center">
                <p
                  className={`text-xs font-medium ${step >= stepItem.number ? "text-brand-blue-600" : "text-gray-500"}`}
                >
                  {stepItem.title}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {stepItem.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Progress Bar */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-600">
              Step {step} of 4
            </span>
            <span className="text-sm text-brand-blue-600 font-semibold">
              {Math.round((step / 4) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-brand-blue-500 to-brand-blue-600 h-2 rounded-full transition-all duration-1000 ease-out"
              style={{
                width: `${(step / 4) * 100}%`,
                animation: "progressFill 1s ease-out forwards",
              }}
            ></div>
          </div>
          <div className="mt-2 text-center">
            <p className="text-sm font-medium text-gray-700">
              {steps[step - 1]?.title}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
const JACK_AT_HOME_ADDRESS = {
  name: "Jack At Home",
  addressLine: "300 Carlsbad Village Dr, Ste 108A",
  city: "Carlsbad",
  stateProvinceCode: "CA",
  postalCode: "92008",
  countryCode: "US",
};
const InitialStep1: React.FC<InitialStepProps> = ({ step, fetchData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [ageConfirmation, setAgeConfirmation] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const [serialNumber, setSerialNumber] = useState<string>("");
  const navigate = useNavigate();

  const [vasectomyDate, setVasectomyDate] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // Use shared hook for shipping address management
  // const { addressData, setAddressData, fieldsEnabled } = useShippingAddress({ reloadOnFocus: true });
  // const { customerName, addressLine, city, stateProvinceCode, postalCode, phone } = addressData;
  // const countryCode = "US"; // Default to US

  const handleVasectomyDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setVasectomyDate(event.target.value);
  };

  // const validateShippingAddress = () => {
  //   const errors: Record<string, string[]> = {};

  //   if (!addressLine) errors.addressLine = ["Street address is required"];
  //   if (!city) errors.city = ["City is required"];
  //   if (!stateProvinceCode) errors.stateProvinceCode = ["State is required"];
  //   if (!postalCode) errors.postalCode = ["ZIP code is required"];

  //   return errors;
  // };

  const buildRequestBody = () => ({
    procedure_date: vasectomyDate,
    customer_address: JACK_AT_HOME_ADDRESS,
  });

  const handleClaimSuccess = (result: any) => {
    if (result.data.status === "completed" && result.data.has_results) {
      notifications.success({
        message:
          "Your kit has been successfully claimed and your results are ready to view!",
      });

      navigate("/results", {
        state: { serial_number: result.data.serial_number },
      });
      return;
    }

    notifications.success({
      message: "The semen analysis kit has been successfully claimed.",
    });

    fetchData();
  };

  const handleClaimError = (response: Response) => {
    if (response.status === 422) {
      setFormErrors({
        serialNumber: [
          "The given serial number does not have the right format, please try again or contact support at support@jackathome.com.",
        ],
      });
      return;
    }

    if (response.status === 404) {
      setFormErrors({
        serialNumber: [
          "The given serial number does not exists, please try again or contact support at support@jackathome.com.",
        ],
      });
      return;
    }

    throw new Error("Failed to load resource");
  };

  const handleConfirmationResponse = async (confirm: boolean) => {
    setShowConfirmation(false);
    if (!confirm) return;

    // const addressErrors = validateShippingAddress();
    // if (Object.keys(addressErrors).length > 0) {
    //   setFormErrors(addressErrors);
    //   notifications.error({
    //     message: "Please fill in all required shipping address fields.",
    //   });
    //   return;
    // }

    try {
      setLoading(true);
      setFormErrors({});

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/status/claimed`;

      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        body: JSON.stringify(buildRequestBody()),
      });

      if (response.ok) {
        const result = await response.json();
        handleClaimSuccess(result);
        return;
      }

      handleClaimError(response);
    } catch (error) {
      console.error("Error fetching data:", error);
      notifications.error({
        message:
          "Something went wrong while trying to claim the semen analysis kit.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isWithinTwelveWeeks = (date: string): boolean => {
    const vasectomyDateObj = new Date(date);
    const twelveWeeksAgo = new Date();
    twelveWeeksAgo.setDate(twelveWeeksAgo.getDate() - 84);
    return vasectomyDateObj > twelveWeeksAgo;
  };

  // const validateAddress = () => {
  //   const rules: Array<[boolean, string, string]> = [
  //     [!addressLine, "addressLine", "Street address is required"],
  //     [!city, "city", "City is required"],
  //     [!stateProvinceCode, "stateProvinceCode", "State is required"],
  //     [!postalCode, "postalCode", "ZIP code is required"],
  //   ];

  //   return rules.reduce<Record<string, string[]>>((errors, [invalid, field, msg]) => {
  //     if (invalid) {
  //       errors[field] = [msg];
  //     }
  //     return errors;
  //   }, {});
  // };

  const handleSuccessResponse = (result: any) => {
    const { status, has_results, serial_number } = result.data;

    const isCompletedWithResults = status === "completed" && has_results;

    notifications.success({
      message: isCompletedWithResults
        ? "Your kit has been successfully claimed and your results are ready to view!"
        : "The semen analysis kit has been successfully claimed.",
    });

    if (isCompletedWithResults) {
      navigate("/results", {
        state: { serial_number },
      });
    } else {
      fetchData();
    }
  };

  const SERIAL_NUMBER_ERROR_MESSAGES: Record<number, string> = {
    422: "The given serial number does not have the right format, please try again or contact support at support@jackathome.com.",
    404: "The given serial number does not exists, please try again or contact support at support@jackathome.com.",
  };

  const handleErrorResponse = (response: Response) => {
    const message = SERIAL_NUMBER_ERROR_MESSAGES[response.status];

    if (message) {
      setFormErrors({
        serialNumber: [message],
      });
      return;
    }

    throw new Error("Failed to load resource");
  };

  const handleOnClickNext = async () => {
    if (isWithinTwelveWeeks(vasectomyDate)) {
      setShowConfirmation(true);
      return;
    }

    // const addressErrors = validateAddress();
    // if (Object.keys(addressErrors).length > 0) {
    //   setFormErrors(addressErrors);
    //   notifications.error({
    //     message: "Please fill in all required shipping address fields.",
    //   });
    //   return;
    // }

    try {
      setLoading(true);
      setFormErrors({});

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/status/claimed`;

      const requestBody = {
        procedure_date: vasectomyDate,
        customer_address: JACK_AT_HOME_ADDRESS,
      };

      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        handleSuccessResponse(result);
        return;
      }

      handleErrorResponse(response);
    } catch (error) {
      console.error("Error fetching data:", error);
      notifications.error({
        message:
          "Something went wrong while trying to claim the semen analysis kit.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="max-w-6xl mx-auto">
        {/* Main Content Card */}
        <div className="bg-white  border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Form Section */}
              <div className="space-y-6">
                <div className="text-center lg:text-left">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    Kit Registration Details
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Please provide the following information to claim your semen
                    analysis kit
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Serial Number Input */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <Input
                      label="Kit Serial Number"
                      name="serialNumber"
                      placeholder="XXXX-XXXX-XXXX"
                      errors={formErrors}
                      value={serialNumber}
                      setValue={setSerialNumber}
                      disabled={loading}
                      inputMask={(inputText: string) => {
                        const cleanText = inputText
                          .toUpperCase()
                          .replaceAll(/[^A-Z0-9]/g, "");

                        let formattedText = "";
                        for (let i = 0; i < cleanText.length; i++) {
                          if (i > 0 && i % 4 === 0) {
                            formattedText += "-";
                          }
                          formattedText += cleanText[i];
                        }

                        return formattedText.slice(0, 14);
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Found on the barcode of your transport tube
                    </p>
                  </div>

                  {/* Vasectomy Date Input */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <label
                      htmlFor="vasectomyDate"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Vasectomy Procedure Date
                    </label>
                    <ImprovedDatePicker
                      id="vasectomyDate"
                      value={vasectomyDate}
                      onChange={handleVasectomyDateChange}
                      disabled={loading}
                      className="w-full"
                      placeholder="mm/dd/yyyy"
                      style={{ minWidth: 137 }}
                      maxDateAllowed={true}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      When was your vasectomy procedure performed?
                    </p>
                  </div>

                  {/* Shipping Address Section (Required - for automatic UPS label generation) */}
                  {/* <div className="bg-brand-blue-50 rounded-xl p-6 border border-brand-blue-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start flex-1">
                        <svg className="h-5 w-5 text-brand-blue-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <div>
                          <h3 className="text-sm font-semibold text-brand-blue-800 mb-1">
                            Shipping Address <span className="text-red-500">*</span>
                          </h3>
                          <p className="text-xs text-brand-blue-700">
                            Provide your address to automatically generate your UPS return label
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigate('/profile-update');
                        }}
                        className="ml-2 p-1.5 text-brand-blue-600 hover:text-brand-blue-800 hover:bg-brand-blue-100 rounded-lg transition-colors duration-200"
                        title="Edit address in profile"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>

                    {!fieldsEnabled && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          Please click the edit icon above to add or update your shipping address in your profile, then return to this page to continue.
                        </p>
                      </div>
                    )}
                    <div className="space-y-4 mt-4">
                      <Input
                        label="Full Name"
                        name="customerName"
                        placeholder="Enter your full name"
                        value={customerName}
                        setValue={(val: string) => setAddressData({ customerName: val })}
                        disabled={loading || !fieldsEnabled}
                        errors={formErrors}
                      />
                      <Input
                        label="Street Address"
                        name="addressLine"
                        placeholder="Enter your street address"
                        value={addressLine}
                        setValue={(val: string) => setAddressData({ addressLine: val })}
                        disabled={loading || !fieldsEnabled}
                        required={true}
                        errors={formErrors}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="City"
                          name="city"
                          placeholder="Enter your city"
                          value={city}
                          setValue={(val: string) => setAddressData({ city: val })}
                          disabled={loading || !fieldsEnabled}
                          required={true}
                          errors={formErrors}
                        />
                        <Input
                          label="State"
                          name="stateProvinceCode"
                          placeholder="Enter state code (e.g., CA)"
                          value={stateProvinceCode}
                          setValue={(val: string) => setAddressData({ stateProvinceCode: val.toUpperCase().slice(0, 2) })}
                          disabled={loading || !fieldsEnabled}
                          required={true}
                          inputMask={(text: string) => text.toUpperCase().slice(0, 2)}
                          errors={formErrors}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="ZIP Code"
                          name="postalCode"
                          placeholder="Enter ZIP code"
                          value={postalCode}
                          setValue={(val: string) => setAddressData({ postalCode: val })}
                          disabled={loading || !fieldsEnabled}
                          required={true}
                          errors={formErrors}
                        />
                        <Input
                          label="Phone (Optional)"
                          name="phone"
                          placeholder="Enter phone number (optional)"
                          value={phone}
                          setValue={(val: string) => setAddressData({ phone: val })}
                          disabled={loading || !fieldsEnabled}
                          errors={formErrors}
                        />
                      </div>
                    </div>
                  </div> */}
                  <div className="bg-brand-blue-50 rounded-xl p-6 border border-brand-blue-200">
                    <h3 className="text-sm font-semibold text-brand-blue-800 mb-2">
                      Return Address
                    </h3>

                    <p className="text-sm text-brand-blue-700">
                      If your package cannot be delivered, it will be returned
                      to:
                    </p>

                    <div className="mt-3 text-sm text-gray-700">
                      <strong>Jack At Home</strong>
                      <br />
                      300 Carlsbad Village Dr, Ste 108A
                      <br />
                      Carlsbad, CA 92008
                    </div>
                  </div>

                  {/* Age Confirmation */}
                  <div className="bg-brand-blue-50 rounded-xl p-6 border border-brand-blue-200">
                    <Checkbox
                      label="I confirm I am 18 years of age or older*"
                      name="confirm"
                      checked={ageConfirmation}
                      onChange={async () => {
                        setAgeConfirmation(!ageConfirmation);
                      }}
                    />
                  </div>

                  {/* Action Button */}
                  {!showConfirmation && (
                    <div className="mt-8 text-center">
                      <Button
                        label={loading ? "Processing..." : "Claim My Kit"}
                        theme="primary"
                        size="large"
                        className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        onClick={handleOnClickNext}
                        disabled={
                          serialNumber.length < 14 ||
                          loading ||
                          vasectomyDate === "" ||
                          !ageConfirmation
                        }
                      />
                      {loading && (
                        <div className="mt-4">
                          <div className="inline-flex items-center text-sm text-gray-600">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-brand-blue-600"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Please wait while we process your request...
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Confirmation Dialog */}
                  {showConfirmation && (
                    <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-lg p-6 mx-auto">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-6 w-6 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            Early Testing Notice
                          </h3>
                          <p className="text-sm text-gray-600 mb-6">
                            It is recommended that you wait at least 12 weeks
                            after your procedure before submitting a sample for
                            testing. Would you still like to continue?
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              className="inline-flex items-center justify-center px-6 py-3 border border-brand-blue-500 text-sm font-medium rounded-lg text-brand-blue-600 bg-white hover:bg-brand-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleConfirmationResponse(true)}
                              disabled={loading}
                            >
                              Yes, Continue
                            </button>
                            <button
                              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleConfirmationResponse(false)}
                              disabled={loading}
                            >
                              Wait 12 Weeks
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Visual Section */}
              <div className="flex flex-col items-center justify-center">
                <div className="bg-gradient-to-br from-brand-blue-50 to-brand-blue-100  p-8 w-full max-w-md">
                  <div className="text-center">
                    <div className="w-24 h-24 bg-brand-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-12 h-12 text-brand-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      You must register your kit in order to receive your
                      results
                    </h3>
                    <p className="text-gray-600 text-sm mb-6">
                      Locate the serial number on your transport tube to get
                      started
                    </p>
                  </div>
                  <img
                    className="w-full max-w-xs mx-auto rounded-lg shadow-md"
                    src="../images/transport.png"
                    alt="Transport Tube with Barcode"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ClaimedStep2: React.FC<StepProps> = ({ step, fetchData, data }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const [trackingNumber, setTrackingNumber] = useState<string>(
    data.tracking_number || "",
  );
  const [editMode, setEditMode] = useState<boolean>(false);

  // UPS Label state
  const [upsLabel, setUpsLabel] = useState<any>(data.ups_label || null);
  const [generatingLabel, setGeneratingLabel] = useState<boolean>(false);
  const [labelError, setLabelError] = useState<string | null>(null);

  // Address form for manual label generation
  // const [showAddressForm, setShowAddressForm] = useState<boolean>(false);

  // Use shared hook for shipping address management
  // const { addressData, setAddressData, loadShippingAddress } = useShippingAddress({ reloadOnFocus: true });
  // const { customerName, addressLine, city, stateProvinceCode, postalCode, phone } = addressData;

  // Update trackingNumber and UPS label when data changes
  useEffect(() => {
    setTrackingNumber(data.tracking_number || "");
    // Only update upsLabel if data.ups_label exists, otherwise preserve existing label
    if (data.ups_label) {
      setUpsLabel(data.ups_label);
    }
    // If we have a label but data doesn't have ups_label, keep the existing label
    // This handles the case where label was just generated but fetchData hasn't updated yet
  }, [data.tracking_number, data.ups_label]);

  // Load shipping address when address form is shown
  // useEffect(() => {
  //   if (showAddressForm) {
  //     // loadShippingAddress();
  //   }
  // }, [showAddressForm, loadShippingAddress]);

  // Helper function to display base64 image
  const getImageSrc = (base64String: string | undefined) => {
    if (!base64String) return null;
    // Handle both data URL format and raw base64
    if (base64String.startsWith("data:")) {
      return base64String;
    }
    return `data:image/gif;base64,${base64String}`;
  };

  // Download label image
  const downloadLabel = (base64Image: string, filename: string) => {
    const link = document.createElement("a");
    link.href = getImageSrc(base64Image) ?? "";
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Generate UPS label manually
  const handleGenerateUpsLabel = async () => {
    // if (!addressLine || !city || !stateProvinceCode || !postalCode) {
    //   setLabelError("Please fill in all required address fields");
    //   return;
    // }

    setGeneratingLabel(true);
    setLabelError(null);

    try {
      const result = await generateUpsReturnLabel(
        data.serial_number,
        JACK_AT_HOME_ADDRESS,
      );

      // Set the label data immediately
      const labelData = {
        trackingNumber: result.trackingNumber,
        labelImage: result.labelImage,
        referenceNumber: result.referenceNumber,
      };
      setUpsLabel(labelData);
      // setShowAddressForm(false);
      notifications.success({
        message:
          "UPS return label generated successfully! Check your email for the label and mobile barcode.",
      });
      // Refresh data to get updated tracking number, but preserve the label
      fetchData();
    } catch (error: any) {
      console.error("Error generating UPS label:", error);
      setLabelError(
        error.message || "Failed to generate UPS label. Please try again.",
      );
      notifications.error({
        message: error.message || "Failed to generate UPS return label",
      });
    } finally {
      setGeneratingLabel(false);
    }
  };

  const handleToggleEdit = () => {
    setEditMode(!editMode);
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      setFormErrors({});

      const response = await updateTrackingNumber(
        data.serial_number,
        trackingNumber,
      );

      if (!response.ok) {
        if (response.status === 422) {
          const errorData = await response.json();
          setFormErrors(mergeErrors(errorData.errors));
        } else {
          throw new Error("Failed to load resource");
        }
      }

      if (response.ok) {
        notifications.success({
          message: "The tracking number has been successfully updated.",
        });
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      notifications.error({
        message:
          "Something went wrong while trying to update the tracking number.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setTrackingNumber(data.tracking_number || trackingNumber);
    setEditMode(false);
    setFormErrors({});
  };

  const handleOnClickNext = async () => {
    setLoading(true);
    await changeStatus(data.serial_number, "sampleCollectionInProgress");
    fetchData();
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="max-w-4xl mx-auto">
        {/* Main Content Card */}
        <div className="bg-white  border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Kit Successfully Claimed!
              </h2>
              <p className="text-gray-600">
                {upsLabel
                  ? "Your UPS return label is ready!"
                  : "Get your UPS return label to ship your kit back"}
              </p>
            </div>

            {/* Printable Label Section */}
            {upsLabel?.labelImage && (
              <div className="mb-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-4 text-center">
                  Printable Return Label
                </h3>

                {/* Email Notification Banner */}
                <div className="mb-4 bg-blue-100 border border-blue-300 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800 mb-1">
                        Check Your Email
                      </p>
                      <p className="text-sm text-blue-700">
                        Your UPS return label has also been sent to your email
                        address. Look for an email from UPS with the subject
                        "Your UPS Return Label and Mobile Barcode" - you can use
                        the mobile barcode at any UPS Store location.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-blue-200 mb-4">
                  <img
                    src={getImageSrc(upsLabel.labelImage) || ""}
                    alt="UPS Return Label"
                    className="w-full max-w-md mx-auto"
                  />
                </div>
                <div className="text-center space-y-2">
                  <Button
                    label="Download Label"
                    theme="primary"
                    size="medium"
                    onClick={() =>
                      downloadLabel(
                        upsLabel.labelImage,
                        `ups-return-label-${upsLabel.trackingNumber}.gif`,
                      )
                    }
                    disabled={loading}
                  />
                  <p className="text-sm text-blue-700">
                    Print this label and attach it to your package, or use the
                    mobile barcode from your email at any UPS Store
                  </p>
                </div>
              </div>
            )}

            {/* Label Already Generated Section (if generated but image not loaded) */}
            {!upsLabel && data.ups_label_generated && data.tracking_number && (
              <div className="mb-8 bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="flex items-start mb-4">
                  <svg
                    className="h-6 w-6 text-green-600 mr-3 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      UPS Return Label Generated
                    </h3>
                    <p className="text-green-700 text-sm mb-4">
                      Your return label has been generated. Tracking Number:{" "}
                      <span className="font-mono font-bold">
                        {data.tracking_number}
                      </span>
                    </p>

                    {/* Email Check Guidance */}
                    <div className="mb-4 bg-green-100 border border-green-300 rounded-lg p-3">
                      <div className="flex items-start">
                        <svg
                          className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-green-800 mb-1">
                            Check Your Email
                          </p>
                          <p className="text-sm text-green-700">
                            Your UPS return label was sent to your email
                            address. Look for an email from UPS with subject
                            "Your UPS Return Label and Mobile Barcode". You can
                            use the mobile barcode from the email at any UPS
                            Store location.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Regenerate Label button should only appear after a failed attempt.
                        In the "label already generated" success state, we intentionally hide it. */}
                  </div>
                </div>
              </div>
            )}

            {/* Generate Label Section (if not generated) */}
            {!upsLabel && !data.ups_label_generated && (
              <div className="mb-8 bg-amber-50 rounded-xl p-6 border border-amber-200">
                <div className="flex items-start mb-4">
                  <svg
                    className="h-6 w-6 text-amber-600 mr-3 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-amber-800 mb-2">
                      Generate Your UPS Return Label
                    </h3>
                    <p className="text-amber-700 text-sm mb-4">
                      You need a return label to ship your kit back to the lab.
                      Click below to generate one now.
                    </p>

                    <Button
                      label={
                        generatingLabel
                          ? "Generating..."
                          : "Generate Return Label"
                      }
                      theme="primary"
                      size="medium"
                      onClick={handleGenerateUpsLabel}
                      disabled={loading || generatingLabel}
                    />

                    {labelError && (
                      <p className="text-sm text-red-600 mt-3">{labelError}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tracking Number Section (if label exists) */}
            {upsLabel && (
              <div className="bg-gradient-to-br from-brand-blue-50 to-brand-blue-100 rounded-xl p-6 border border-brand-blue-200 mb-6">
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-brand-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-brand-blue-800 mb-2">
                      Tracking Information
                    </h3>
                    <p className="text-brand-blue-700 text-sm">
                      Your tracking number is saved. You can track your shipment
                      using the number below.
                    </p>
                  </div>
                </div>

                {editMode ? (
                  <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                    <div className="space-y-4">
                      <Input
                        label="Tracking Number"
                        name="tracking_number"
                        placeholder="XXXX-XXXX-XXXX"
                        errors={formErrors}
                        value={trackingNumber}
                        setValue={setTrackingNumber}
                        disabled={!editMode || loading}
                        inputMask={(inputText: string) => {
                          const cleanText = inputText
                            .toUpperCase()
                            .replaceAll(/[^A-Z0-9]/g, "");

                          let formattedText = "";
                          for (let i = 0; i < cleanText.length; i++) {
                            if (i > 0 && i % 4 === 0) {
                              formattedText += "-";
                            }
                            formattedText += cleanText[i];
                          }

                          return formattedText.slice(0, 14);
                        }}
                      />

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg
                            className="h-5 w-5 text-yellow-600 mr-3 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                          </svg>
                          <p className="text-yellow-800 text-sm">
                            Please ensure the tracking number is accurate;
                            otherwise, you won't receive updates until the
                            package arrives at the laboratory.
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <Button
                          label="Save Changes"
                          theme="primary"
                          size="medium"
                          className="flex-1"
                          onClick={handleSaveChanges}
                          disabled={loading || trackingNumber.length === 0}
                        />
                        <Button
                          label="Cancel"
                          theme="secondary"
                          size="medium"
                          className="flex-1"
                          onClick={handleCancelEdit}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data.tracking_number || trackingNumber ? (
                      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">
                              Current Tracking Number
                            </p>
                            <p className="text-xl font-mono font-semibold text-gray-800 tracking-wider">
                              {data.tracking_number || trackingNumber}
                            </p>
                          </div>
                          <Button
                            label="Edit"
                            theme="secondary"
                            size="small"
                            className="ml-4"
                            onClick={handleToggleEdit}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                        <div className="flex items-center">
                          <svg
                            className="h-5 w-5 text-amber-600 mr-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                          </svg>
                          <div className="flex-1">
                            <p className="text-amber-800 text-sm">
                              No tracking number is currently associated with
                              your kit. Please add one by clicking{" "}
                              <button
                                className="text-amber-600 hover:text-amber-700 font-semibold underline ml-1"
                                onClick={handleToggleEdit}
                                disabled={loading}
                              >
                                here
                              </button>
                              .
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="mb-8 text-center">
            <Button
              label={
                loading ? "Processing..." : "Continue to Sample Collection"
              }
              theme="primary"
              size="large"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              onClick={handleOnClickNext}
              disabled={loading || (!upsLabel && !trackingNumber)}
            />
            {!upsLabel && !trackingNumber && (
              <p className="text-sm text-amber-600 mt-2">
                Please generate your UPS return label before continuing
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const SampleCollectionInProgressStep3: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sampleCollectionDate, setSampleCollectionDate] = useState<Date | null>(
    new Date(),
  );

  const saveSampleCollectionTimesamp = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${data.serial_number}/sample-collection-timestamp`;

      const response = await fetch(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        method: "PATCH",
        body: JSON.stringify({
          sample_collection_timestamp: sampleCollectionDate?.toISOString(),
        }),
      });

      if (!response.ok) {
        if (response.status === 422) {
          const result = await response.json();

          throw new Error(
            `Validation error: ${JSON.stringify(mergeErrors(result.errors))}.`,
          );
        } else {
          throw new Error("Failed to load resource.");
        }
      }

      if (response.ok) {
        return true;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    return false;
  };

  const handleOnClickNext = async () => {
    setLoading(true);
    if (await saveSampleCollectionTimesamp()) {
      await changeStatus(data.serial_number, "preparingForShippingToLab");
      fetchData();
    }
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="max-w-6xl mx-auto">
        {/* Main Content Card */}
        <div className="bg-white  border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-brand-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-brand-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Sample Collection
              </h2>
              <p className="text-gray-600">
                Follow the instructions and record when you collected your
                sample
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Instructions Section */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-brand-blue-50 to-brand-blue-100 rounded-xl p-6 border border-brand-blue-200">
                  <div className="flex items-start mb-4">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-brand-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-brand-blue-800 mb-2">
                        Important Instructions
                      </h3>
                      <p className="text-brand-blue-700 text-sm mb-4">
                        Please follow the detailed instructions that came with
                        your kit to collect your semen sample properly.
                      </p>
                      <a
                        href="https://jackathome.com/pages/instructions"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-brand-blue-600 hover:text-brand-blue-700 font-semibold text-sm transition-colors duration-200"
                      >
                        View Detailed Instructions
                        <svg
                          className="ml-1 w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-green-800 mb-2">
                        Ready to Record Collection Time?
                      </h4>
                      <p className="text-green-700 text-sm">
                        Once you've followed the instructions and your sample
                        has been correctly collected, please input the exact
                        date and time of collection below.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Collection Date & Time
                  </h3>
                  <div className="space-y-4">
                    <DateTimePicker
                      amPmAriaLabel="Select AM/PM"
                      calendarAriaLabel="Toggle calendar"
                      clearAriaLabel="Clear value"
                      dayAriaLabel="Day"
                      hourAriaLabel="Hour"
                      maxDetail="second"
                      minuteAriaLabel="Minute"
                      monthAriaLabel="Month"
                      nativeInputAriaLabel="Date and time"
                      onChange={setSampleCollectionDate}
                      secondAriaLabel="Second"
                      value={sampleCollectionDate}
                      yearAriaLabel="Year"
                    />
                    <p className="text-xs text-gray-500">
                      Select the exact date and time when you collected your
                      sample
                    </p>
                  </div>
                </div>
              </div>

              {/* Visual and Form Section */}
              <div className="space-y-6">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-6">
                    <img
                      className="w-full max-w-xs mx-auto rounded-lg shadow-md"
                      src="../images/fist.png"
                      alt="Sample Collection Instructions"
                    />
                  </div>
                </div>
                {/* Action Button */}
                <div className="mt-8 text-center">
                  <Button
                    label={
                      loading ? "Processing..." : "Sample Collected - Continue"
                    }
                    theme="primary"
                    size="large"
                    className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    onClick={handleOnClickNext}
                    disabled={loading || !sampleCollectionDate}
                  />
                  {loading && (
                    <div className="mt-4">
                      <div className="inline-flex items-center text-sm text-gray-600">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-brand-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Recording your collection time...
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PreparingForShippingToLabStep4: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  const navigate = useNavigate();
  const [isShipping, setIsShipping] = useState(false);

  const handleShippingConfirmation = async () => {
    setIsShipping(true);
    // Simulate a brief delay for better UX
    setTimeout(() => {
      notifications.success({
        message:
          "Great! We'll keep an eye out for your kit. You'll be notified via email when your test results are ready.",
      });
      navigate("/history");
    }, 1500);
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="max-w-6xl mx-auto">
        {/* Main Content Card */}
        <div className="bg-white  border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Ready to Ship!
              </h2>
              <p className="text-gray-600">
                Package your sample and send it to our laboratory
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Instructions Section */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-start mb-4">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        Final Steps
                      </h3>
                      <div className="space-y-3 text-green-700 text-sm">
                        <div className="flex items-start">
                          <span className="font-semibold mr-2">1.</span>
                          <span>Securely seal the kit box</span>
                        </div>
                        <div className="flex items-start">
                          <span className="font-semibold mr-2">2.</span>
                          <span>
                            Affix the shipping label over the Jack logo
                          </span>
                        </div>
                        <div className="flex items-start">
                          <span className="font-semibold mr-2">3.</span>
                          <span>
                            Drop off at your nearest UPS store{" "}
                            <strong>today</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">
                        Important Reminder
                      </h4>
                      <p className="text-blue-700 text-sm">
                        Please ship your kit today to ensure the sample remains
                        viable for testing. We'll track your package and notify
                        you when it arrives at our laboratory.
                      </p>
                    </div>
                  </div>
                </div>
                {/* Action Button */}
                <div className="mt-8 text-center">
                  <Button
                    label={isShipping ? "Processing..." : "I've Mailed My Kit"}
                    theme="primary"
                    size="large"
                    className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    onClick={handleShippingConfirmation}
                    disabled={isShipping}
                  />
                  {isShipping && (
                    <div className="mt-4">
                      <div className="inline-flex items-center text-sm text-gray-600">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Confirming your shipment...
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Visual Section */}
              <div className="flex flex-col items-center justify-center">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 w-full max-w-md">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      Package Your Kit
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Follow the packaging instructions carefully
                    </p>
                  </div>
                  <img
                    className="w-full max-w-xs mx-auto rounded-lg shadow-md"
                    src="../images/return-box.png"
                    alt="Return Box Packaging"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ShippingToLabInProgressStep5: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full lg:w-1/2 mt-12">
        <p>
          The sample is on the way to the laboratory, we will keep you updated.
        </p>
        <img
          className="mt-6 w-120 h-120"
          src="../images/sample_shipping.png"
          alt="Sample"
        />
      </div>
    </>
  );
};

const ShippingToLabCompletedStep6: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full lg:w-1/2 mt-12">
        <p>
          The sample has arrived to the laboratory, we will keep you updated.
        </p>
        <img
          className="mt-6 w-120 h-120"
          src="../images/sample_in_lab.png"
          alt="Sample"
        />
      </div>
    </>
  );
};

const SuccessfullyProcessedByLabStep7: React.FC<StepProps> = ({
  step,
  fetchData,
  data,
}) => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);

  const handleCheckboxChange = async () => {
    setChecked(!checked);
  };

  const handleOnClickNext = async () => {
    setDisableNextButton(true);
    setLoading(true);
    await changeStatus(data.serial_number, "completed");
    notifications.success({
      message:
        "The active analysis has been successfully completed. We're taking you to your results!",
    });
    const resultsFromApi = await fetchResults(data.serial_number);
    navigate("/results", { state: { data: resultsFromApi } });
    fetchData();
    setLoading(false);
  };

  return (
    <>
      <ProgressBar step={step} />

      <div className="w-full mt-6">
        <p className="text-base leading-relaxed font-bold text-gray-950 mb-6 mt-6">
          Almost done! Just a quick thumbs-up to the terms and conditions below,
          and we can show you the results:
        </p>
        <Checkbox
          label="I understand and accept that by exporting or printing my medical
          exam results, I take full responsibility for the security of my
          personal medical data, as once it is outside the system, CARE FOR
          THERE LLC cannot guarantee its protection to comply with HIPAA
          (Health Insurance Portability and Accountability Act) privacy
          regulations. I agree that any unauthorized disclosure of this
          information is at my own risk."
          name="agree"
          checked={checked}
          disabled={loading}
          onChange={handleCheckboxChange}
        />
      </div>
      <div className="w-full mt-24">
        <Button
          label="Finalize"
          theme="primary"
          size="medium"
          className="block w-full md:ml-auto md:w-auto"
          onClick={handleOnClickNext}
          disabled={!checked || loading || disableNextButton}
        />
      </div>
    </>
  );
};

const Active = () => {
  const [fetchDataTrigger, setFetchDataTrigger] = useState<number>(0);
  const [step, setStep] = useState<string>("initial");
  const [data, setData] = useState<AnalysisKitDataSchema | null>(null);
  const [loading, setLoading] = useState(true);

  /*useEffect(() => {
    setFetchDataTrigger(Date.now())
  }, [])*/

  useEffect(() => {
    const { abortController, abortSignal } = createAbortController();

    setLoading(true);

    fetchInProgressData(abortSignal)
      .then((data) => {
        if (data) {
          setData(data);
          setStep(data.status);
        } else {
          setData(null);
          setStep("initial");
          notifications.info({
            message:
              "No active analysis found, you are now about to start a new analysis.",
          });
        }

        setLoading(false);
      })
      .catch(console.error);

    return () => {
      abortController.abort();
    };
  }, [fetchDataTrigger]);

  const fetchDataTriggerHandle = () => {
    setFetchDataTrigger(Date.now());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb
          pages={[{ name: "Kit Registration", href: "#", current: true }]}
        />

        {loading && (
          <div className="flex justify-center items-center min-h-96">
            <div className="text-center">
              <CenteredLoader className="flex justify-center mb-4 mt-40" />
              <p className="text-gray-600">Loading your kit information...</p>
            </div>
          </div>
        )}

        {!loading && step === "initial" && (
          <InitialStep1 step={1} fetchData={fetchDataTriggerHandle} />
        )}
        {!loading && data && step === "claimed" && (
          <ClaimedStep2
            step={2}
            fetchData={fetchDataTriggerHandle}
            data={data}
          />
        )}
        {!loading && data && step === "sampleCollectionInProgress" && (
          <SampleCollectionInProgressStep3
            step={3}
            fetchData={fetchDataTriggerHandle}
            data={data}
          />
        )}
        {!loading && data && step === "preparingForShippingToLab" && (
          <PreparingForShippingToLabStep4
            step={4}
            fetchData={fetchDataTriggerHandle}
            data={data}
          />
        )}
        {!loading && data && step === "shippingToLabInProgress" && (
          <ShippingToLabInProgressStep5
            step={5}
            fetchData={fetchDataTriggerHandle}
            data={data}
          />
        )}
        {!loading && data && step === "shippingToLabCompleted" && (
          <ShippingToLabCompletedStep6
            step={6}
            fetchData={fetchDataTriggerHandle}
            data={data}
          />
        )}
        {!loading && data && step === "successfullyProcessedByLab" && (
          <SuccessfullyProcessedByLabStep7
            step={7}
            fetchData={fetchDataTriggerHandle}
            data={data}
          />
        )}
      </main>
    </div>
  );
};

export default Active;
